import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  codingRoundService,
  CodingRoundData,
  Question,
  SubmitQuestionRequest,
} from '@/services/api/codingRoundService';

interface CodingRoundState {
  data: CodingRoundData | null;
  currentProblemIndex: number;
  viewedQuestions: number[];
  isLoading: boolean;
  isSubmitting: boolean;
  fetchError: string | null;
  submitError: string | null;
  languageMetadata: Record<string, { pistonLang: string; version: string }>;
}

const getInitialQuestionIndex = () => {
  const stored = sessionStorage.getItem('currentQuestionIndex');
  if (stored) {
    const index = parseInt(stored, 10);
    return isNaN(index) ? 0 : index;
  }
  return 0;
};

const initialIndex = getInitialQuestionIndex();

const initialState: CodingRoundState = {
  data: null,
  currentProblemIndex: initialIndex,
  viewedQuestions: [initialIndex],
  isLoading: false,
  isSubmitting: false,
  fetchError: null,
  submitError: null,
  languageMetadata: {},
};

export const fetchCodingQuestions = createAsyncThunk(
  'codingRound/fetchQuestions',
  async (candidateId: string, { rejectWithValue }) => {
    try {
      const response = await codingRoundService.getQuestions(candidateId);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch questions';
      return rejectWithValue(message);
    }
  }
);

export const submitCodingQuestion = createAsyncThunk(
  'codingRound/submitQuestion',
  async (
    params: SubmitQuestionRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const response = await codingRoundService.submitQuestion(params);

      // Find the index of the question to update its status locally
      const state = getState() as { codingRound: CodingRoundState };
      const questions = state.codingRound.data?.questions || [];
      const questionIndex = questions.findIndex(
        q => q.id === params.question_id
      );

      if (questionIndex !== -1) {
        dispatch(
          updateQuestionStatus({ index: questionIndex, status: 'Submitted' })
        );
      }

      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to submit question';
      return rejectWithValue(message);
    }
  }
);

export const fetchPistonRuntimes = createAsyncThunk(
  'codingRound/fetchRuntimes',
  async (_, { rejectWithValue }) => {
    try {
      const runtimes = await codingRoundService.getPistonRuntimes();

      // Target languages for filtering and mapping
      const targetMap: Record<string, string> = {
        javascript: 'javascript',
        node: 'javascript',
        python: 'python',
        java: 'java',
        c: 'c',
        cpp: 'cpp',
        'c++': 'cpp',
        csharp: 'csharp',
        'c#': 'csharp',
        mono: 'csharp',
      };

      const metadata: Record<string, { pistonLang: string; version: string }> =
        {};

      runtimes.forEach(runtime => {
        const normalizedName = runtime.language.toLowerCase();
        const mappedName = targetMap[normalizedName];

        if (mappedName && !metadata[mappedName]) {
          metadata[mappedName] = {
            pistonLang: mappedName,
            version: runtime.version,
          };
        }
      });

      return metadata;
    } catch (error: unknown) {
      return rejectWithValue('Failed to fetch runtime metadata');
    }
  }
);

const codingRoundSlice = createSlice({
  name: 'codingRound',
  initialState,
  reducers: {
    setCurrentProblemIndex: (state, action: PayloadAction<number>) => {
      state.currentProblemIndex = action.payload;
      if (!state.viewedQuestions.includes(action.payload)) {
        state.viewedQuestions.push(action.payload);
      }
    },
    updateQuestionStatus: (
      state,
      action: PayloadAction<{ index: number; status: Question['status'] }>
    ) => {
      if (state.data) {
        state.data.questions[action.payload.index].status =
          action.payload.status;
      }
    },
    resetCodingRound: state => {
      state.data = null;
      state.currentProblemIndex = 0;
      state.viewedQuestions = [0];
      state.isLoading = false;
      state.isSubmitting = false;
      state.fetchError = null;
      state.submitError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCodingQuestions.pending, state => {
        state.isLoading = true;
        state.fetchError = null;
      })
      .addCase(fetchCodingQuestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.fetchError = null;
      })
      .addCase(fetchCodingQuestions.rejected, (state, action) => {
        state.isLoading = false;
        state.fetchError = action.payload as string;
      });

    // Submit Question
    builder
      .addCase(submitCodingQuestion.pending, state => {
        state.isSubmitting = true;
        state.submitError = null;
      })
      .addCase(submitCodingQuestion.fulfilled, state => {
        state.isSubmitting = false;
        state.submitError = null;
      })
      .addCase(submitCodingQuestion.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError = action.payload as string;
      });

    // Fetch Runtimes
    builder.addCase(fetchPistonRuntimes.fulfilled, (state, action) => {
      state.languageMetadata = action.payload;
    });
  },
});

export const { setCurrentProblemIndex } = codingRoundSlice.actions;
const { updateQuestionStatus } = codingRoundSlice.actions;

export default codingRoundSlice.reducer;
